import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useParams } from 'react-router';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  IconButton,
  makeStyles,
  TableRow,
  TableCell,
  Button,
  Link,
  Typography,
  Menu,
  MenuItem,
} from '@material-ui/core';
import moment from 'moment';
import * as Icons from '@material-ui/icons';

function getTo(params, ticketId) {
  const { queueId } = params;
  const baseUrl = '/app/queue';

  if (queueId) {
    return `${baseUrl}/${queueId}/${ticketId}`;
  }

  return `${baseUrl}/all/${ticketId}`;
}

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: 'center',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    cursor: 'pointer',
    textDecoration: 'none',
  },
  cell: {
    borderBottom: 'none',
    padding: 0,
    overflow: 'hidden',
  },
  link: {
    display: 'block',
    padding: theme.spacing(1) + 1000,
    margin: -1000,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  link2: {
    display: 'block',
    padding: theme.spacing(1) + 1000,
    margin: -1000,
    textDecoration: 'underline',
  },
}));

const QueueItem = ({
  className,
  ticket,
  allColumns,
  setRowColour,
  clearRowColour,
  ...rest
}) => {
  const classes = useStyles();
  const params = useParams();
  const to = getTo(params, ticket.id);

  const [showRightClickMenu, setShowRightClickMenu] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const getIcon = (name, colour) => {
    const Icon = Icons[name];
    if (colour !== null) return <Icon style={{ color: colour }} />;

    return <Icon />;
  };

  const cellRightClicked = (e) => {
    e.preventDefault();

    setAnchorEl(e.currentTarget);
    setShowRightClickMenu(true);
  };

  const getColumnValue = (column) => {
    if (column.key === 'Type') {
      return (
        <TableCell
          className={classes.cell}
          title={ticket.type}
          key={column.key}
          underline="none"
          style={{ textDecoration: 'none' }}
          onContextMenu={(e) => cellRightClicked(e)}
        >
          <Link
            component={RouterLink}
            to={to}
            className={classes.link}
            color="textSecondary"
          >
            <IconButton size="small" className={classes.action}>
              {getIcon(ticket.typeIcon, null)}
            </IconButton>
          </Link>
        </TableCell>
      );
    }
    if (column.key === 'Priority') {
      return (
        <TableCell
          className={classes.cell}
          title={ticket.priority}
          key={column.key}
          underline="none"
          style={{ textDecoration: 'none' }}
          onContextMenu={(e) => cellRightClicked(e)}
        >
          <Link
            component={RouterLink}
            to={to}
            className={classes.link}
            color="textSecondary"
          >
            <IconButton size="small" className={classes.action}>
              {getIcon(ticket.priorityIcon, ticket.priorityColour)}
            </IconButton>
          </Link>
        </TableCell>
      );
    }
    if (column.key === 'Status') {
      return (
        <TableCell
          className={classes.cell}
          style={{ minWidth: 200, maxWidth: 200, textDecoration: 'none' }}
          title={ticket.status}
          key={column.key}
          underline="none"
          onContextMenu={(e) => cellRightClicked(e)}
        >
          <Link
            component={RouterLink}
            to={to}
            className={classes.link}
            color="textSecondary"
          >
            <Button
              variant="contained"
              fullWidth
              style={{
                backgroundColor: ticket.statusColour,
                color: '#FFFFFF',
                height: 20,
              }}
            >
              <Typography
                variant="h6"
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {ticket.status}
              </Typography>
            </Button>
          </Link>
        </TableCell>
      );
    }
    if (column.key === 'Reporter') {
      return (
        <TableCell className={classes.cell} key={column.key}>
          <Link
            color="textSecondary"
            component={RouterLink}
            to={`/app/management/customers/${ticket.reporter?.id}`}
            className={classes.link2}
            variant="h6"
          >
            {ticket.reporter?.firstName}
            {' '}
            {ticket.reporter?.lastName}
          </Link>
        </TableCell>
      );
    }
    if (column.key === 'Assignee') {
      return (
        <TableCell className={classes.cell} key={column.key}>
          {ticket.assignee.id === null ? (
            <Link
              component={RouterLink}
              to={to}
              className={classes.link}
              color="textSecondary"
            >
              <i>Unassigned</i>
            </Link>
          ) : (
            <Link
              color="textSecondary"
              component={RouterLink}
              to={`/app/management/customers/${ticket.assignee?.id}`}
              variant="h6"
              className={classes.link2}
            >
              {ticket.assignee?.firstName}
              {' '}
              {ticket.assignee?.lastName}
            </Link>
          )}
        </TableCell>
      );
    }
    if (column.key === 'Organisation') {
      return (
        <TableCell className={classes.cell} key={column.key}>
          {ticket.organisationId === null ? (
            <Link
              component={RouterLink}
              to={to}
              className={classes.link}
              color="textSecondary"
            />
          ) : (
            <Link
              color="textSecondary"
              component={RouterLink}
              to={`/app/management/organisation/${ticket.organisationId}`}
              className={classes.link2}
              variant="h6"
            >
              {ticket.organisation}
            </Link>
          )}
        </TableCell>
      );
    }
    if (column.key === 'CreatedDateTime') {
      return (
        <TableCell
          className={classes.cell}
          title={moment(ticket.createdDateTime).format('DD/MMM/YY hh:mm A')}
          key={column.key}
          underline="none"
          style={{ textDecoration: 'none' }}
          onContextMenu={(e) => cellRightClicked(e)}
        >
          <Link
            component={RouterLink}
            to={to}
            className={classes.link}
            color="textSecondary"
          >
            {moment(ticket.createdDateTime).format('DD/MMM/YY')}
          </Link>
        </TableCell>
      );
    }
    if (column.key === 'LastModifiedDateTime') {
      return (
        <TableCell
          className={classes.cell}
          title={moment(ticket.lastModifiedDateTime).format(
            'DD/MMM/YY hh:mm A'
          )}
          key={column.key}
          underline="none"
          style={{ textDecoration: 'none' }}
          onContextMenu={(e) => cellRightClicked(e)}
        >
          <Link
            component={RouterLink}
            to={to}
            className={classes.link}
            color="textSecondary"
          >
            {moment(ticket.lastModifiedDateTime).format('DD/MMM/YY')}
          </Link>
        </TableCell>
      );
    }
    if (column.key === 'ResolvedDateTime') {
      return (
        <TableCell
          className={classes.cell}
          title={moment(ticket.resolvedDateTime).format('DD/MMM/YY hh:mm A')}
          key={column.key}
          underline="none"
          style={{ textDecoration: 'none' }}
          onContextMenu={(e) => cellRightClicked(e)}
        >
          <Link
            component={RouterLink}
            to={to}
            className={classes.link}
            color="textSecondary"
          >
            {moment(ticket.resolvedDateTime).format('DD/MMM/YY')}
          </Link>
        </TableCell>
      );
    }
    if (column.key === 'LoggedTime') {
      return (
        <TableCell
          className={classes.cell}
          title={ticket.loggedTime}
          key={column.key}
          underline="none"
          style={{ textDecoration: 'none' }}
          onContextMenu={(e) => cellRightClicked(e)}
        >
          <Link
            component={RouterLink}
            to={to}
            className={classes.link}
            color="textSecondary"
          >
            {ticket.loggedTime}
          </Link>
        </TableCell>
      );
    }

    return (
      <TableCell
        className={classes.cell}
        key={column.key}
        underline="none"
        style={{ textDecoration: 'none' }}
        onContextMenu={(e) => cellRightClicked(e)}
      >
        <Link
          component={RouterLink}
          to={to}
          className={classes.link}
          color="textSecondary"
        >
          {ticket[column.key.toLowerCase()]}
        </Link>
      </TableCell>
    );
  };

  return (
    <>
      <Menu
        id="fade-menu"
        anchorEl={anchorEl}
        keepMounted
        open={showRightClickMenu}
        onClose={() => {
          setShowRightClickMenu(false);
          setAnchorEl(null);
        }}
      >
        <MenuItem
          onClick={() => {
            setShowRightClickMenu(false);
            setRowColour(ticket);
          }}
        >
          Set Row Colour
        </MenuItem>
        {ticket.highlightRowColour === '' ? null : (
          <MenuItem
            onClick={() => {
              setShowRightClickMenu(false);
              clearRowColour(ticket);
            }}
          >
            Clear Row Colour
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setShowRightClickMenu(false);
            window.open(to, '_blank');
          }}
        >
          Open In New Tab
        </MenuItem>
      </Menu>

      <TableRow
        className={clsx(classes.root, className)}
        {...rest}
        style={{ backgroundColor: ticket.highlightRowColour }}
      >
        {allColumns.map((column) => getColumnValue(column))}
      </TableRow>
    </>
  );
};

QueueItem.propTypes = {
  className: PropTypes.string,
  ticket: PropTypes.object.isRequired,
  allColumns: PropTypes.array.isRequired,
  setRowColour: PropTypes.func,
  clearRowColour: PropTypes.func,
};

export default QueueItem;
